import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';

export interface Booking {
  id: string;
  booking_code: string | null;
  tour_id: string | null;
  tour_title: string;
  destination: string;
  full_name: string;
  email: string;
  phone: string;
  adults: number;
  children: number;
  travel_date: string;
  special_requests: string;
  total_price: number;
  status: string;
  cancelled_by: string | null;
  created_at: string;
}

export function useBookings() {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let channel: ReturnType<typeof supabase.channel> | null = null;

    async function fetchBookings(showLoading: boolean) {
      if (showLoading) setLoading(true);
      setError(null);

      try {
        const { data } = await supabase.auth.getUser();
        const user = data.user;

        if (!user) {
          if (!cancelled) setBookings([]);
          return;
        }

        const { data: rows, error: err } = await supabase
          .from('vietnamtravel_bookings')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (err) throw err;
        if (!cancelled) setBookings((rows || []) as Booking[]);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load bookings');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchBookings(true);

    // Realtime: when an admin updates a booking (e.g. changes status), the
    // traveler's profile reflects it instantly without a manual refresh.
    supabase.auth.getSession().then(({ data }) => {
      const uid = data.session?.user?.id;
      if (!uid || cancelled) return;

      channel = supabase
        .channel(`user-bookings-${uid}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'vietnamtravel_bookings', filter: `user_id=eq.${uid}` },
          () => fetchBookings(false),
        )
        .subscribe();
    });

    return () => {
      cancelled = true;
      if (channel) supabase.removeChannel(channel);
    };
  }, [refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  const cancelBooking = async (bookingId: string) => {
    const { data: authData } = await supabase.auth.getUser();
    const user = authData.user;
    if (!user) return { error: 'You must be signed in to cancel a booking.' };

    const { error: err } = await supabase
      .from('vietnamtravel_bookings')
      .update({ status: 'Cancelled', cancelled_by: 'user' })
      .eq('id', bookingId)
      .eq('user_id', user.id);

    if (err) return { error: err.message };

    setBookings((prev) =>
      prev.map((b) =>
        b.id === bookingId ? { ...b, status: 'Cancelled', cancelled_by: 'user' } : b,
      ),
    );
    return { error: null };
  };

  return { bookings, loading, error, refresh, cancelBooking };
}